import { useCallback, useContext, useEffect, useState } from 'react';
import { InputLabel, styled } from '@mui/material';

// Contexts
import { LanguageContext } from '../../../core/TenantProvider/contexts';
// Components - Atoms, Molecules, Organisms, Pages
import { APUploadedFileDetails } from '../APUploadedFileDetails';
import { AEUploadFileSection } from '../APUploadFileSection';
// Types
import { TranslateFunction } from '../../../core/types/TypographyTypes';
// Utils
import getRoundedFileSize from '../../../core/utils/GetRoundedFileSize';
import { ValidateFileExtension, ValidateFileSize } from '../../../core/utils/GetFileValidation';
import TranslateWrapper from '../../../core/utils/TranslateWrapper';

export type APDocumentUploadProps = {
  onFileUpload: (fileData: any) => void,
  errorMessage: string | null,
  inputLabel: string,
  removeFileUpload: boolean,
  required: boolean,
  removedFile?: Function
};

const StyledInputLabel = styled(InputLabel)({
  marginBottom: 8
});

const APDocumentUpload = (props: APDocumentUploadProps) => {
  const { onFileUpload, errorMessage, inputLabel, removeFileUpload, required, removedFile } = props;

  const { translations } = useContext(LanguageContext);
  const translate: TranslateFunction = TranslateWrapper(translations);

  const [uploadedFileName, setUploadedFileName] = useState<string>('');
  const [uploadedFileSize, setUploadedFileSize] = useState<string>('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedFileError, setUploadedFileError] = useState<string | null>(errorMessage);

  const allowedFileTypesErrorText: string = translate('documentUpload.allowedFileTypes');
  const maxFileSizeErrorText: string = translate('documentUpload.maxFileSize');

  const handleFileErrors = (isFileSizeValid: boolean, isFileExtensionValid: boolean) => {
    const hasNoErrors: boolean = isFileSizeValid && isFileExtensionValid;

    if (hasNoErrors) {
      return false;
    }

    const handleErrors = (errorText: string) => {
      removeUploadedFile();
      setUploadedFileError(errorText);
    };

    if (!isFileSizeValid) {
      handleErrors(maxFileSizeErrorText);
    } else if (!isFileExtensionValid) {
      handleErrors(allowedFileTypesErrorText);
    }

    return true;
  };

  const handleFileUpload = (files: FileList) => {
    const fileUploaded: File = files[0];
    const fileName: string = fileUploaded.name;
    const roundedFileSize: string = getRoundedFileSize(fileUploaded.size);

    setUploadedFileName(fileName);
    setUploadedFileSize(roundedFileSize);
    setUploadedFile(fileUploaded);

    const isFileSizeValid: boolean = ValidateFileSize(fileUploaded);
    const isFileExtensionValid: boolean = ValidateFileExtension(fileUploaded);

    if (handleFileErrors(isFileSizeValid, isFileExtensionValid)) {
      return;
    }

    setUploadedFileError(null);
    onFileUpload?.({ fileName, roundedFileSize, fileUploaded, files });
  };

  const removeUploadedFile = useCallback(() => {
    setUploadedFileName('');
    setUploadedFileSize('');
    setUploadedFile(null);

    if (removedFile) {
      removedFile(true);
    }
  }, [removedFile]);

  const errorShow: string = errorMessage ?? '';

  useEffect(() => {
    if (errorShow || removeFileUpload) {
      removeUploadedFile();
    }
  }, [errorShow, removeFileUpload, removeUploadedFile]);

  const labelSuffix: string = required ? '*' : '';
  const labelText: string = inputLabel + labelSuffix;
  const haveFileName: string | boolean = uploadedFileName && uploadedFileName.length > 0;
  const uploadedFileErrorText: string = uploadedFileError ?? '';

  return (
    <>
      <StyledInputLabel htmlFor="input-label">{labelText}</StyledInputLabel>
      {haveFileName ? (
        <APUploadedFileDetails
          fileName={uploadedFileName}
          fileSize={uploadedFileSize}
          removeUploadedFileHandler={removeUploadedFile}
        />
      ) : (
        <AEUploadFileSection
          handleFileUpload={handleFileUpload}
          value={uploadedFile}
          error={uploadedFileErrorText} />
      )}
    </>
  );
};

export default APDocumentUpload;
